import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import {
  Box,
  Chip,
  Dialog,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";

/* ═══════════════════════════════════════════════════════════════════════════
   REUSABLE EXPLORE GALLERY MODAL
   Premium sliding carousel with touch/swipe, autoplay, keyboard nav
   ═══════════════════════════════════════════════════════════════════════════ */

const T = {
  navy: "#0f172a",
  gold: "#d4a373",
  goldLight: "#f8f5f0",
  cream: "#f8f5f0",
  white: "#ffffff",
  bg: "#f8f5f0",
  slate: "#6b7280",
  slateLt: "#9ca3af",
  line: "#e5e7eb",
};

const TAG_COLORS: Record<string, { bg: string; color: string }> = {
  "Popular": { bg: "#f8f5f0", color: "#a16207" },
  "Luxury": { bg: "#f8f5f0", color: "#6b7280" },
  "Premium": { bg: "#f8f5f0", color: "#0f172a" },
  "Standard": { bg: "#f8f5f0", color: "#6b7280" },
  "Recommended": { bg: "#f8f5f0", color: "#d4a373" },
  "Chef Recommended": { bg: "#f8f5f0", color: "#d4a373" },
};

const SLIDE_MS = 350;
const AUTOPLAY_MS = 5000;
const EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

export interface GalleryItem {
  image: string;
  title: string;
  tag?: string;
  category?: string;
  mediaType?: "image" | "video";
  video?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  priceLabel: string;
  heroImage: string;
  items: GalleryItem[];
  categories?: string[];
}

const ExploreGalleryModal: React.FC<Props> = ({
  open, onClose, title, subtitle, priceLabel, heroImage, items, categories: catsProp,
}) => {
  const [activeTab, setActiveTab] = useState("All");
  const [viewIdx, setViewIdx] = useState<number | null>(null);

  /* Transition state: prevIdx + direction for simultaneous crossfade */
  const [prevIdx, setPrevIdx] = useState<number | null>(null);
  const [slideDir, setSlideDir] = useState<1 | -1>(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const animTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [autoplay, setAutoplay] = useState(true);
  const [hovered, setHovered] = useState(false);

  /* Touch / swipe refs */
  const touchStartX = useRef(0);
  const touchDeltaX = useRef(0);
  const [dragOffset, setDragOffset] = useState(0);
  const isDragging = useRef(false);

  /* Edge bounce */
  const [bounceDir, setBounceDir] = useState<"left" | "right" | null>(null);

  /* Autoplay timer */
  const autoplayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /* Modal open animation */
  const [modalReady, setModalReady] = useState(false);
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => setModalReady(true));
    } else {
      setModalReady(false);
      setViewIdx(null);
      setPrevIdx(null);
      setActiveTab("All");
      setAutoplay(true);
      setIsAnimating(false);
      if (animTimerRef.current) clearTimeout(animTimerRef.current);
    }
  }, [open]);

  const categories = useMemo(() => {
    if (catsProp && catsProp.length > 0) return ["All", ...catsProp];
    const cats = Array.from(new Set(items.filter((i) => i.tag).map((i) => i.tag!)));
    return cats.length > 1 ? ["All", ...cats] : [];
  }, [items, catsProp]);

  const filteredItems = useMemo(
    () => activeTab === "All" ? items : items.filter((i) => i.tag === activeTab || i.category === activeTab),
    [items, activeTab],
  );

  /* Navigate with simultaneous crossfade */
  const slideTo = useCallback((newIdx: number, dir: 1 | -1) => {
    if (isAnimating || filteredItems.length <= 1 || viewIdx === null) return;
    if (newIdx === viewIdx) return;

    /* Preload the target image before starting animation */
    const img = new Image();
    img.src = filteredItems[newIdx].image.replace(/w=\d+&h=\d+/, "w=1200&h=800");

    setPrevIdx(viewIdx);
    setSlideDir(dir);
    setViewIdx(newIdx);
    setIsAnimating(true);

    if (animTimerRef.current) clearTimeout(animTimerRef.current);
    animTimerRef.current = setTimeout(() => {
      setIsAnimating(false);
      setPrevIdx(null);
    }, SLIDE_MS + 20);
  }, [isAnimating, filteredItems, viewIdx]);

  const goNext = useCallback(() => {
    if (viewIdx === null) return;
    slideTo((viewIdx + 1) % filteredItems.length, 1);
  }, [viewIdx, filteredItems.length, slideTo]);

  const goPrev = useCallback(() => {
    if (viewIdx === null) return;
    slideTo((viewIdx - 1 + filteredItems.length) % filteredItems.length, -1);
  }, [viewIdx, filteredItems.length, slideTo]);

  const goToThumb = useCallback((idx: number) => {
    if (viewIdx === null || idx === viewIdx || isAnimating) return;
    slideTo(idx, idx > viewIdx ? 1 : -1);
  }, [viewIdx, isAnimating, slideTo]);

  /* Arrow click with edge bounce */
  const handleNext = useCallback(() => {
    goNext();
    if (viewIdx !== null && viewIdx === filteredItems.length - 1) {
      setBounceDir("right");
      setTimeout(() => setBounceDir(null), 300);
    }
  }, [goNext, viewIdx, filteredItems.length]);

  const handlePrev = useCallback(() => {
    goPrev();
    if (viewIdx === 0) {
      setBounceDir("left");
      setTimeout(() => setBounceDir(null), 300);
    }
  }, [goPrev, viewIdx]);

  /* Keyboard */
  useEffect(() => {
    if (viewIdx === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") handleNext();
      else if (e.key === "ArrowLeft") handlePrev();
      else if (e.key === "Escape") { setViewIdx(null); setPrevIdx(null); setIsAnimating(false); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [viewIdx, handleNext, handlePrev]);

  /* Autoplay */
  useEffect(() => {
    if (autoplayRef.current) clearInterval(autoplayRef.current);
    if (viewIdx !== null && autoplay && !hovered && filteredItems.length > 1) {
      autoplayRef.current = setInterval(goNext, AUTOPLAY_MS);
    }
    return () => { if (autoplayRef.current) clearInterval(autoplayRef.current); };
  }, [viewIdx, autoplay, hovered, goNext, filteredItems.length]);

  /* Preload adjacent images */
  useEffect(() => {
    if (viewIdx === null || filteredItems.length <= 1) return;
    const preload = (idx: number) => {
      const img = new Image();
      img.src = filteredItems[idx].image.replace(/w=\d+&h=\d+/, "w=1200&h=800");
    };
    preload((viewIdx + 1) % filteredItems.length);
    if (filteredItems.length > 2) preload((viewIdx - 1 + filteredItems.length) % filteredItems.length);
  }, [viewIdx, filteredItems]);

  /* Touch handlers */
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
    isDragging.current = true;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
    setDragOffset(touchDeltaX.current);
  };
  const onTouchEnd = () => {
    isDragging.current = false;
    const threshold = 60;
    if (touchDeltaX.current < -threshold) handleNext();
    else if (touchDeltaX.current > threshold) handlePrev();
    setDragOffset(0);
    touchDeltaX.current = 0;
  };

  /* Unique animation names to avoid conflicts */
  const exitAnimName = `egm-exit-${slideDir > 0 ? "l" : "r"}`;
  const enterAnimName = `egm-enter-${slideDir > 0 ? "l" : "r"}`;

  /* ── Gallery grid view ── */
  const renderGrid = () => (
    <>
      {/* Hero Banner */}
      <Box
        sx={{
          position: "relative", height: { xs: 150, sm: 200 },
          backgroundImage: `url(${heroImage})`, backgroundSize: "cover", backgroundPosition: "center",
        }}
      >
        <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(13,27,42,0.88) 0%, rgba(13,27,42,0.35) 50%, rgba(13,27,42,0.1) 100%)" }} />
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", top: 12, right: 12, bgcolor: "rgba(255,255,255,0.9)", backdropFilter: "blur(8px)", width: 36, height: 36, "&:hover": { bgcolor: T.white } }}
        >
          <CloseRoundedIcon sx={{ fontSize: 18, color: T.navy }} />
        </IconButton>
        <Box sx={{ position: "absolute", bottom: 18, left: 24, right: 24 }}>
          <Typography sx={{ fontSize: { xs: 22, sm: 28 }, fontWeight: 800, color: T.white, lineHeight: 1.1, textShadow: "0 2px 8px rgba(0,0,0,0.3)" }}>
            {title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.8 }}>
            <Chip label={priceLabel} size="small" sx={{ height: 24, fontSize: 11, fontWeight: 700, bgcolor: T.gold, color: T.white }} />
            {items.length > 0 && (
              <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>
                {items.length} style{items.length !== 1 ? "s" : ""} available
              </Typography>
            )}
          </Box>
          {subtitle && <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.6)", mt: 0.5, fontStyle: "italic" }}>{subtitle}</Typography>}
        </Box>
      </Box>

      {/* Category Tabs */}
      {categories.length > 2 && (
        <Box sx={{ px: 2, pt: 1, bgcolor: T.white, borderBottom: `1px solid ${T.line}` }}>
          <Tabs
            value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto"
            sx={{
              minHeight: 38,
              "& .MuiTab-root": { textTransform: "none", fontSize: 12.5, fontWeight: 600, minHeight: 38, py: 0, color: T.slate, "&.Mui-selected": { color: T.gold } },
              "& .MuiTabs-indicator": { bgcolor: T.gold, height: 2.5, borderRadius: "2px 2px 0 0" },
            }}
          >
            {categories.map((cat) => <Tab key={cat} label={cat} value={cat} />)}
          </Tabs>
        </Box>
      )}

      {/* Gallery Grid */}
      <Box sx={{ p: { xs: 2, sm: 3 }, overflow: "auto", maxHeight: "58vh" }}>
        {filteredItems.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <CollectionsOutlinedIcon sx={{ fontSize: 44, color: T.slateLt, opacity: 0.4 }} />
            <Typography sx={{ fontSize: 14, color: T.slateLt, mt: 1 }}>No gallery images yet</Typography>
            <Typography sx={{ fontSize: 11, color: T.slateLt, mt: 0.3 }}>Images can be added in the admin panel</Typography>
          </Box>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }, gap: 2 }}>
            {filteredItems.map((item, i) => (
              <Box
                key={`${item.title}-${i}`}
                onClick={() => { setSlideDir(1); setViewIdx(i); }}
                sx={{
                  borderRadius: "14px", overflow: "hidden", cursor: "pointer",
                  bgcolor: T.white, border: `1px solid ${T.line}`,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  transition: "all 0.25s cubic-bezier(0.22, 1, 0.36, 1)",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 28px rgba(0,0,0,0.12)",
                    "& .gallery-img": { transform: "scale(1.06)" },
                    "& .view-overlay": { opacity: 1 },
                  },
                }}
              >
                <Box sx={{ position: "relative", aspectRatio: "4 / 3", overflow: "hidden" }}>
                  <Box className="gallery-img" sx={{ width: "100%", height: "100%", backgroundImage: `url(${item.image})`, backgroundSize: "cover", backgroundPosition: "center", transition: "transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)" }} />
                  {item.tag && (
                    <Chip
                      icon={(item.tag === "Popular" || item.tag === "Recommended") ? <StarRoundedIcon sx={{ fontSize: 11, color: `${TAG_COLORS[item.tag]?.color || T.gold} !important` }} /> : undefined}
                      label={item.tag} size="small"
                      sx={{ position: "absolute", top: 10, left: 10, height: 24, fontSize: 10, fontWeight: 700, bgcolor: TAG_COLORS[item.tag]?.bg || T.goldLight, color: TAG_COLORS[item.tag]?.color || T.navy, backdropFilter: "blur(6px)", "& .MuiChip-icon": { ml: "3px" } }}
                    />
                  )}
                  {item.mediaType === "video" && item.video && (
                    <Box sx={{ position: "absolute", bottom: 10, right: 10, width: 28, height: 28, borderRadius: "50%", bgcolor: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid rgba(255,255,255,0.2)" }}>
                      <PlayArrowRoundedIcon sx={{ color: T.white, fontSize: 16, ml: "1px" }} />
                    </Box>
                  )}
                  <Box className="view-overlay" sx={{ position: "absolute", inset: 0, bgcolor: "rgba(13,27,42,0.35)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.25s ease" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, bgcolor: "rgba(255,255,255,0.92)", borderRadius: "20px", px: 1.8, py: 0.6, backdropFilter: "blur(6px)" }}>
                      {item.mediaType === "video" ? <PlayArrowRoundedIcon sx={{ fontSize: 14, color: T.navy }} /> : <VisibilityOutlinedIcon sx={{ fontSize: 14, color: T.navy }} />}
                      <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.navy }}>{item.mediaType === "video" ? "Play" : "View"}</Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ px: 1.5, py: 1.2 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy, lineHeight: 1.2 }}>{item.title}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </>
  );

  /* ── Sliding carousel viewer ── */
  const renderViewer = () => {
    if (viewIdx === null || !filteredItems[viewIdx]) return null;
    const current = filteredItems[viewIdx];
    const single = filteredItems.length <= 1;

    /* Exit direction: slide to opposite of travel */
    const exitX = slideDir === 1 ? "-45%" : "45%";
    /* Enter direction: slide from direction of travel */
    const enterX = slideDir === 1 ? "45%" : "-45%";

    return (
      <Box
        sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: T.navy, overflow: "hidden", userSelect: "none" }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Top bar */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.5, flexShrink: 0 }}>
          <Box onClick={() => { setViewIdx(null); setPrevIdx(null); setIsAnimating(false); }} sx={{ display: "flex", alignItems: "center", gap: 0.8, cursor: "pointer", "&:hover .back-arrow": { transform: "translateX(-2px)" } }}>
            <ArrowBackIosNewRoundedIcon className="back-arrow" sx={{ fontSize: 14, color: "rgba(255,255,255,0.6)", transition: "transform 0.2s" }} />
            <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.6)", fontWeight: 500 }}>Back to gallery</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {!single && (
              <IconButton onClick={() => setAutoplay(!autoplay)} sx={{ width: 28, height: 28, color: "rgba(255,255,255,0.4)", "&:hover": { color: "rgba(255,255,255,0.7)" } }}>
                {autoplay ? <PauseRoundedIcon sx={{ fontSize: 14 }} /> : <PlayArrowRoundedIcon sx={{ fontSize: 14 }} />}
              </IconButton>
            )}
            <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>{viewIdx + 1} / {filteredItems.length}</Typography>
            <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.5)", "&:hover": { color: T.white } }}>
              <CloseRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Progress bar (autoplay indicator) */}
        {autoplay && !single && !hovered && (
          <Box sx={{ height: 2, mx: 2.5, mb: 0.5, borderRadius: 1, bgcolor: "rgba(255,255,255,0.08)", overflow: "hidden", flexShrink: 0 }}>
            <Box
              key={`prog-${viewIdx}`}
              sx={{
                height: "100%", bgcolor: T.gold, borderRadius: 1, width: "100%",
                animation: `progFill ${AUTOPLAY_MS}ms linear`,
                "@keyframes progFill": { "0%": { width: "0%" }, "100%": { width: "100%" } },
              }}
            />
          </Box>
        )}

        {/* Main sliding image area */}
        <Box
          sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", position: "relative", minHeight: 0, px: { xs: 1, sm: 5 }, overflow: "hidden" }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Nav arrows */}
          {!single && (
            <>
              <IconButton
                onClick={handlePrev}
                sx={{
                  position: "absolute", left: { xs: 4, sm: 12 }, zIndex: 10,
                  bgcolor: "rgba(255,255,255,0.06)", width: 48, height: 48,
                  opacity: hovered ? 1 : 0,
                  transition: "all 0.25s ease",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.14)", transform: "scale(1.08)" },
                  ...(bounceDir === "left" && {
                    animation: `egm-bounce-l 300ms ease`,
                    "@keyframes egm-bounce-l": {
                      "0%": { transform: "translateX(0)" },
                      "40%": { transform: "translateX(-6px)" },
                      "100%": { transform: "translateX(0)" },
                    },
                  }),
                }}
              >
                <ArrowBackIosNewRoundedIcon sx={{ color: T.white, fontSize: 18 }} />
              </IconButton>
              <IconButton
                onClick={handleNext}
                sx={{
                  position: "absolute", right: { xs: 4, sm: 12 }, zIndex: 10,
                  bgcolor: "rgba(255,255,255,0.06)", width: 48, height: 48,
                  opacity: hovered ? 1 : 0,
                  transition: "all 0.25s ease",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.14)", transform: "scale(1.08)" },
                  ...(bounceDir === "right" && {
                    animation: `egm-bounce-r 300ms ease`,
                    "@keyframes egm-bounce-r": {
                      "0%": { transform: "translateX(0)" },
                      "40%": { transform: "translateX(6px)" },
                      "100%": { transform: "translateX(0)" },
                    },
                  }),
                }}
              >
                <ArrowForwardIosRoundedIcon sx={{ color: T.white, fontSize: 18 }} />
              </IconButton>
            </>
          )}

          {/* Image carousel container */}
          <Box
            sx={{
              position: "relative", width: "100%", maxWidth: 820,
              aspectRatio: "16 / 9",
              borderRadius: "14px", overflow: "hidden",
              boxShadow: "0 16px 56px rgba(0,0,0,0.45)",
              transform: dragOffset ? `translateX(${dragOffset * 0.5}px)` : undefined,
              transition: dragOffset ? "none" : `transform ${SLIDE_MS}ms ${EASE}`,
            }}
          >
            {/* EXITING image (previous) — slides out with fade + scale + blur */}
            {isAnimating && prevIdx !== null && filteredItems[prevIdx] && (
              filteredItems[prevIdx].video ? (
                <Box
                  component="video"
                  key={`exit-vid-${prevIdx}`}
                  src={filteredItems[prevIdx].video}
                  muted
                  playsInline
                  sx={{
                    position: "absolute", inset: 0, width: "100%", height: "100%",
                    objectFit: "cover", zIndex: 2,
                    willChange: "transform, opacity",
                    animation: `${exitAnimName} ${SLIDE_MS}ms ${EASE} forwards`,
                    [`@keyframes ${exitAnimName}`]: {
                      "0%": { transform: "translateX(0) scale(1)", opacity: 1, filter: "blur(0px)" },
                      "100%": { transform: `translateX(${exitX}) scale(0.94)`, opacity: 0, filter: "blur(2px)" },
                    },
                  }}
                />
              ) : (
                <Box
                  key={`exit-img-${prevIdx}`}
                  sx={{
                    position: "absolute", inset: 0,
                    backgroundImage: `url(${filteredItems[prevIdx].image.replace(/w=\d+&h=\d+/, "w=1200&h=800")})`,
                    backgroundSize: "cover", backgroundPosition: "center",
                    zIndex: 2,
                    willChange: "transform, opacity",
                    animation: `${exitAnimName} ${SLIDE_MS}ms ${EASE} forwards`,
                    [`@keyframes ${exitAnimName}`]: {
                      "0%": { transform: "translateX(0) scale(1)", opacity: 1, filter: "blur(0px)" },
                      "100%": { transform: `translateX(${exitX}) scale(0.94)`, opacity: 0, filter: "blur(2px)" },
                    },
                  }}
                />
              )
            )}

            {/* ENTERING / CURRENT image — slides in with fade + scale */}
            {current.mediaType === "video" && current.video ? (
              <>
                {/* Image fallback behind video */}
                <Box
                  sx={{
                    position: "absolute", inset: 0,
                    backgroundImage: `url(${current.image.replace(/w=\d+&h=\d+/, "w=1200&h=800")})`,
                    backgroundSize: "cover", backgroundPosition: "center",
                    zIndex: 1,
                  }}
                />
                <video
                  key={`vid-${viewIdx}`}
                  src={current.video}
                  autoPlay
                  muted
                  loop
                  playsInline
                  style={{
                    position: "absolute", top: 0, left: 0,
                    width: "100%", height: "100%",
                    objectFit: "cover", zIndex: 2,
                  }}
                />
              </>
            ) : (
              <Box
                key={`img-${viewIdx}`}
                sx={{
                  position: "absolute", inset: 0,
                  backgroundImage: `url(${current.image.replace(/w=\d+&h=\d+/, "w=1200&h=800")})`,
                  backgroundSize: "cover", backgroundPosition: "center",
                  zIndex: 1,
                  willChange: "transform, opacity",
                  ...(isAnimating ? {
                    animation: `${enterAnimName} ${SLIDE_MS}ms ${EASE} both`,
                    [`@keyframes ${enterAnimName}`]: {
                      "0%": { transform: `translateX(${enterX}) scale(1.02)`, opacity: 0 },
                      "100%": { transform: "translateX(0) scale(1)", opacity: 1 },
                    },
                  } : {
                    /* Gentle Ken Burns on static display */
                    transform: "scale(1.02)",
                    transition: `transform 6s ease-out`,
                  }),
                }}
              />
            )}

            {/* Gradient overlay for caption */}
            <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "40%", background: "linear-gradient(to top, rgba(13,27,42,0.7) 0%, transparent 100%)", zIndex: 5, pointerEvents: "none" }} />
          </Box>
        </Box>

        {/* Caption */}
        <Box
          key={`cap-${viewIdx}`}
          sx={{
            textAlign: "center", py: 1.5, flexShrink: 0,
            animation: `egm-capFade ${SLIDE_MS}ms ease`,
            "@keyframes egm-capFade": { "0%": { opacity: 0, transform: "translateY(6px)" }, "100%": { opacity: 1, transform: "translateY(0)" } },
          }}
        >
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: T.white }}>{current.title}</Typography>
          {current.tag && (
            <Chip label={current.tag} size="small" sx={{ mt: 0.5, height: 22, fontSize: 10, fontWeight: 600, bgcolor: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.8)" }} />
          )}
        </Box>

        {/* Thumbnail strip */}
        {!single && (
          <Box sx={{
            display: "flex", gap: 0.8, justifyContent: "center", px: 2, pb: 2, pt: 0.5,
            overflowX: "auto", flexShrink: 0,
            "&::-webkit-scrollbar": { height: 3 },
            "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(255,255,255,0.2)", borderRadius: 2 },
          }}>
            {filteredItems.map((item, i) => (
              <Box
                key={i}
                onClick={() => goToThumb(i)}
                sx={{
                  width: 56, height: 42, borderRadius: "8px", overflow: "hidden",
                  cursor: "pointer", flexShrink: 0, position: "relative",
                  border: i === viewIdx ? `2px solid ${T.gold}` : "2px solid transparent",
                  opacity: i === viewIdx ? 1 : 0.35,
                  transform: i === viewIdx ? "scale(1.08)" : "scale(1)",
                  transition: "all 0.25s cubic-bezier(0.22, 1, 0.36, 1)",
                  "&:hover": { opacity: 0.75, transform: "scale(1.04)" },
                }}
              >
                <Box component="img" src={item.image.replace(/w=\d+&h=\d+/, "w=100&h=75")} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                {item.mediaType === "video" && item.video && (
                  <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(0,0,0,0.2)" }}>
                    <PlayArrowRoundedIcon sx={{ color: T.white, fontSize: 14 }} />
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: viewIdx !== null ? "95vw" : { xs: "95vw", sm: "90vw", md: "900px" },
          maxWidth: viewIdx !== null ? "95vw" : 900,
          height: viewIdx !== null ? "92vh" : "auto",
          maxHeight: "92vh",
          borderRadius: viewIdx !== null ? "16px" : "18px",
          overflow: "hidden",
          bgcolor: viewIdx !== null ? T.navy : T.cream,
          transition: "all 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
          /* Modal open animation */
          ...(modalReady ? {
            animation: `egm-modalIn 350ms ${EASE}`,
            "@keyframes egm-modalIn": {
              "0%": { transform: "scale(0.92)", opacity: 0 },
              "100%": { transform: "scale(1)", opacity: 1 },
            },
          } : {}),
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: "rgba(13,27,42,0.6)",
            backdropFilter: "blur(8px)",
            animation: `egm-backdropIn 350ms ease`,
            "@keyframes egm-backdropIn": {
              "0%": { opacity: 0 },
              "100%": { opacity: 1 },
            },
          },
        },
      }}
    >
      {viewIdx !== null ? renderViewer() : renderGrid()}
    </Dialog>
  );
};

export default ExploreGalleryModal;
